import { useEffect, useRef } from 'react';

// Switching back to a tab fires both `visibilitychange` and `focus`; within this window
// they count as one return.
const SAME_RETURN_WINDOW_MS = 1000;

/**
 * Calls `callback` whenever the user comes back to the page — the tab becomes visible
 * or the window regains focus.
 *
 * This replaces polling for data that only has to be current when someone looks at it:
 * an idle or background tab makes no requests at all.
 */
export function useOnPageActive(callback: () => void): void {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    let lastCalledAt = 0;
    const handleActive = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastCalledAt < SAME_RETURN_WINDOW_MS) return;
      lastCalledAt = now;
      callbackRef.current();
    };
    document.addEventListener('visibilitychange', handleActive);
    window.addEventListener('focus', handleActive);
    return () => {
      document.removeEventListener('visibilitychange', handleActive);
      window.removeEventListener('focus', handleActive);
    };
  }, []);
}
